import { Text } from '@thanx/uikit/text'
import type { MakeGoodOffer } from 'api/makeGoodLadder.types'
import type { AvailableReward } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo } from 'react'
import MakeGoodLadder, { LadderRow } from './MakeGoodLadder'
import type { RegenerateErrorKind } from './regenerateError'
import RewardMultiSelect from './RewardMultiSelect'
import { buildRewardMultiSelectLabels } from './RewardMultiSelect/labels'

const t = buildTranslate('recovery_ai.settings.approved_rewards')

type Props = {
  /** ACTIVE-filtered rewards to choose from (the host owns the active filter). */
  rewards: AvailableReward[]
  /** Controlled selection of approved reward ids. */
  value: string[]
  /** Fires the next id array; the host stages/persists it (the body never saves). */
  onChange: (next: string[]) => void
  /** Make-good snapshot rows (AI-330/AI-383) — the ladder's sole source. */
  makeGoodOffers: MakeGoodOffer[]
  /** Declines a proffered offer (the ladder "×"); an immediate write in the host. */
  onDeclineMakeGood: (id: string) => void
  /** True while a decline mutation is in flight (disables every row's "×"). */
  isDecliningMakeGood: boolean
  /**
   * Regenerates the make-good ladder — the section-level control at the bottom
   * of the ladder (AI-331); an immediate write in the host. Restores the full
   * recommended set, so it's how the merchant recovers after declining every
   * reward.
   */
  onRegenerateMakeGood: () => void
  /** True while the regenerate mutation is in flight (disables the button). */
  isRegeneratingMakeGood: boolean
  /**
   * The make-good snapshot fetch SUCCEEDED (RTK Query `isSuccess`). Lets the
   * ladder tell "200 with zero offers" (actionable — offer to generate) from
   * "the BE is unreachable" (stay hidden).
   */
  makeGoodOffersLoaded: boolean
  /**
   * Kind of the last make-good generate/regenerate failure (409 conflict vs any
   * other error), or undefined when it hasn't failed. Threaded to the ladder,
   * which owns the copy.
   */
  makeGoodRegenerateErrorKind?: RegenerateErrorKind
  /**
   * Whether the last make-good decline/disable write failed. Threaded to the
   * ladder, which owns the copy — same treatment as
   * makeGoodRegenerateErrorKind, just a boolean (decline has no non-terminal
   * status to special-case).
   */
  makeGoodDeclineError?: boolean
  /**
   * Read-only mode (AI-64 CASL gate): the host has `access` but not `manage` on
   * RecoveryAI. Threaded to BOTH writable surfaces — the ladder (decline "×" +
   * Regenerate) and the picker (trigger + chip "×") — so no edit affordance in
   * this body stays live. Defaults false so the wizard and the Agent-Tuning
   * modal call sites are unchanged.
   */
  disabled?: boolean
}

// Chrome-free Approved-rewards body (AI-168). Extracted from ApprovedRewardsPane
// so the SAME control renders both as the Settings page form and inside
// ConfigModalHost. Pure + controlled: no fetch, no spinner, no error chrome, no
// PUT — it renders the make-good ladder (AI-172/AI-247/AI-330) above the shared
// RewardMultiSelect and delegates every picker edit to `onChange`. Ladder
// declines are a SEPARATE immediate write, delegated to `onDeclineMakeGood`.
const ApprovedRewardsBody = ({
  rewards,
  value,
  onChange,
  makeGoodOffers,
  onDeclineMakeGood,
  isDecliningMakeGood,
  onRegenerateMakeGood,
  isRegeneratingMakeGood,
  makeGoodOffersLoaded,
  makeGoodRegenerateErrorKind,
  makeGoodDeclineError,
  disabled = false,
}: Props) => {
  // Split the host's active rewards. Make-good rewards (auto-provisioned by
  // Thanx AI, AvailableReward.ai_generated === true) are NEVER picker items
  // (CONTRACT §4). Read as a STRICT boolean — never truthy-coerce an absent field.
  const makeGoodRewards = useMemo(
    () => rewards.filter(r => r.ai_generated === true),
    [rewards]
  )
  const selectableRewards = useMemo(
    () => rewards.filter(r => !r.ai_generated),
    [rewards]
  )
  const makeGoodIdSet = useMemo(
    () => new Set(makeGoodRewards.map(r => r.id)),
    [makeGoodRewards]
  )
  // O(1) membership for the selected-count guard below — mirrors the prior
  // rewardIdSet, now scoped to the selectable set the picker actually shows.
  const selectableIdSet = useMemo(
    () => new Set(selectableRewards.map(r => r.id)),
    [selectableRewards]
  )

  // The merchant's own picks = approved ids that are NOT make-good.
  const pickerValue = useMemo(
    () => value.filter(id => !makeGoodIdSet.has(id)),
    [value, makeGoodIdSet]
  )

  // Any make-good id already in the approved set, re-emitted verbatim on every
  // picker change so a Save can never drop it — the config PUT REPLACES the whole
  // join set, and dropping a make-good id un-provisions that live reward
  // server-side (AI-247). A no-op on the snapshot path (make-goods live in the
  // ladder, not approved_reward_ids), but a real backstop for legacy/grandfathered
  // configs whose ids still sit in the join. Never touched by the ladder ×, which
  // is a separate write.
  const preservedMakeGoodIds = useMemo(
    () => value.filter(id => makeGoodIdSet.has(id)),
    [value, makeGoodIdSet]
  )

  // The ladder's normalized rows — the make-good snapshot is authoritative,
  // sorted ascending by sortOrder (CONTRACT §1), carrying declined/materialized
  // so the ladder can hide declined rows and give materialized ones their own
  // "disable" aria-label (materialized rows keep the "×" too — AI-1121).
  const ladderRows = useMemo<LadderRow[]>(
    () =>
      [...makeGoodOffers]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(offer => ({
          id: offer.id,
          label: offer.name,
          // upToCents IS guest value (not cost) — nexus
          // available_rewards.rb:47 serves the ladder's value_cents as the
          // full up_to_cents. See LadderRow.valueCents for the full citation.
          valueCents: offer.upToCents,
          declined: offer.declined,
          materialized: offer.materialized,
        })),
    [makeGoodOffers]
  )

  // Count only picker selections still present in the selectable set — mirrors
  // the pane so the "N rewards selected" label can't count a stale id. The
  // count/label describe the picker, which no longer contains make-good rewards.
  const selectedCount = useMemo(
    () => pickerValue.filter(id => selectableIdSet.has(id)).length,
    [pickerValue, selectableIdSet]
  )

  const labels = useMemo(
    () => buildRewardMultiSelectLabels(selectedCount),
    [selectedCount]
  )

  const onToggle = useCallback(
    (id: string) => {
      const nextPickerIds = pickerValue.includes(id)
        ? pickerValue.filter(rewardId => rewardId !== id)
        : [...pickerValue, id]
      onChange([...preservedMakeGoodIds, ...nextPickerIds])
    },
    [pickerValue, preservedMakeGoodIds, onChange]
  )

  const onRemove = useCallback(
    (id: string) =>
      onChange([
        ...preservedMakeGoodIds,
        ...pickerValue.filter(rewardId => rewardId !== id),
      ]),
    [pickerValue, preservedMakeGoodIds, onChange]
  )

  return (
    <>
      {/* Make-good ladder (AI-172/AI-247/AI-330/AI-383). Renders null when
          nothing is visible, degrading to picker-only. */}
      <MakeGoodLadder
        rows={ladderRows}
        onDecline={onDeclineMakeGood}
        isDeclining={isDecliningMakeGood}
        onRegenerate={onRegenerateMakeGood}
        isRegenerating={isRegeneratingMakeGood}
        isLoaded={makeGoodOffersLoaded}
        regenerateError={makeGoodRegenerateErrorKind}
        declineError={makeGoodDeclineError}
        disabled={disabled}
      />

      {/* Grantable-rewards section — flat (no ConfigCard) so it sits flush with
          the baseline above (Figma parity): a small bold heading + 14px subtitle
          over the picker, not a bordered card. */}
      <div className="d-flex flex-column">
        <Text.BodyText3 bold>{t('card_title')}</Text.BodyText3>
        <Text.BodyText4 color="grey70" className="mt-xs mb-xs">
          {t('card_desc')}
        </Text.BodyText4>
        <RewardMultiSelect
          rewards={selectableRewards}
          selectedIds={pickerValue}
          onToggle={onToggle}
          onRemove={onRemove}
          labels={labels}
          disabled={disabled}
        />
      </div>
    </>
  )
}

export default ApprovedRewardsBody
